import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import {
  Box,
  Checkbox,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import styles from "../styles/autorizationPayments.module.scss";
import { IRespDocuments } from "shared";
import { ITableElement } from "shared/components";
import DownloadIcon from "@mui/icons-material/Download";
import { FooterTable } from "shared/components/table/components/FooterTable";

interface Props {
  provider: string;
  providerCode: string;
  records: IRespDocuments[];
  columns: ITableElement<any>[];
  contentSize: number;
  totalElements: number;
  currentPage: number;
  currentSize: number;
  isLoading: boolean;
  rowSelected: number[];
  selectAllRows: (
    event: React.ChangeEvent<HTMLInputElement>,
    rows: any,
  ) => void;
  selectRow: (
    event: React.ChangeEvent<HTMLInputElement>,
    id: any,
    row: any,
  ) => void;
  showBtnGenerateCertificate: boolean;
  onGenerateCertificate: () => void;
  getProviderInvoices: (providerCode: string, filters: any, page: number, size: number) => Promise<void>;
  setProviderInvoicesPage: (providerCode: string, page: number) => void;
  setProviderInvoicesSize: (providerCode: string, size: number) => void;
  filters: any;
}

export const CollapseRow = (props: Props) => {
  const {
    provider,
    providerCode,
    records,
    columns,
    contentSize,
    totalElements,
    currentPage,
    currentSize,
    isLoading,
    rowSelected,
    selectAllRows,
    selectRow,
    showBtnGenerateCertificate,
    onGenerateCertificate,
    getProviderInvoices,
    setProviderInvoicesPage,
    setProviderInvoicesSize,
    filters,
  } = props;
  const [open, setOpen] = useState(false);

  const handleExpand = () => {
    setOpen(!open);
    if (!open && records.length === 0) {
      getProviderInvoices(providerCode, filters, 0, currentSize);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setProviderInvoicesPage(providerCode, newPage);
    getProviderInvoices(providerCode, filters, newPage, currentSize);
  };

  const handleChangeSizePage = (event: any) => {
    const newSize = parseInt(event.target.value);
    setProviderInvoicesSize(providerCode, newSize);
    setProviderInvoicesPage(providerCode, 0);
    getProviderInvoices(providerCode, filters, 0, newSize);
  };

  const selectedCountForProvider = useMemo(() => {
    if (!records?.length) return 0;
    const recordIds = new Set(records.map((r: any) => r.id));
    return rowSelected.filter((id) => recordIds.has(id)).length;
  }, [records, rowSelected]);

  const isAllSelectedForProvider =
    selectedCountForProvider > 0 && selectedCountForProvider === contentSize;
  const isIndeterminateForProvider =
    selectedCountForProvider > 0 && selectedCountForProvider < contentSize;
  return (
    <React.Fragment key={provider}>
      <TableRow className={styles.tablaHeader}>
        <TableCell width={50} padding="checkbox">
          <IconButton size="small" onClick={handleExpand}>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell colSpan={4} className={styles.headerCell}>
          <Checkbox
            color="primary"
            indeterminate={isIndeterminateForProvider}
            checked={isAllSelectedForProvider}
            onChange={(event) => selectAllRows(event, records)}
            inputProps={{
              "aria-label": "select all docs",
            }}
            data-testid="checkbox-select-all"
            id={"selectAll"}
          />
          {provider}{open && ` (${totalElements})`}
          <Tooltip
            title="Clic aqui para  generar Certificado de Auditoría de Cuentas Médicas"
            placement="right"
            arrow
          >
            <IconButton
              className={styles.downloadBtn}
              sx={{
                visibility: showBtnGenerateCertificate ? "visible" : "hidden",
              }}
              data-testid="btn-generate-certificate"
              onClick={onGenerateCertificate}
            >
              <DownloadIcon color="primary" fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ p: 0 }} colSpan={5}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box className={styles.tableResults}>
              {isLoading ? (
                <Box sx={{ p: 3, textAlign: "center" }}>Cargando facturas...</Box>
              ) : (
                <>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell> </TableCell>
                        {columns.map((column, index) => (
                          <TableCell
                            align="left"
                            className={styles.tableCell}
                            style={{
                              minWidth: column.minWidth,
                              display: column.isHidden ? "none" : "",
                            }}
                            key={index}
                          >
                            {column.headerName}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody data-testid="table-results-body">
                      {records.map((row: any) => (
                        <TableRow key={row.id}>
                          <TableCell
                            padding="checkbox"
                            style={{
                              padding: "0",
                            }}
                          >
                            <Checkbox
                              color="primary"
                              inputProps={{
                                "aria-labelledby": `check${row.id}`,
                              }}
                              checked={rowSelected.indexOf(row.id) !== -1}
                              onChange={(e) => selectRow(e, row.id, row)}
                              className={styles.checkboxFiles}
                              id={`check${row.id}`}
                            />
                          </TableCell>
                          {columns.map((column, index) => (
                            <TableCell
                              key={index}
                              align="left"
                              className={styles.tableCell}
                              style={{
                                minWidth: column.minWidth,
                                display: column.isHidden ? "none" : "",
                              }}
                            >
                              {column.renderCell
                                ? column.renderCell(row)
                                : row[column.field] != null
                                  ? String(row[column.field])
                                  : ""}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {records.length > 0 && (
                    <FooterTable
                      handleChangePage={handleChangePage}
                      handleChangeSizePage={handleChangeSizePage}
                      numberPage={currentPage}
                      size={currentSize}
                      sizeRows={currentSize.toString()}
                      totalElements={totalElements}
                      valuesResultsPages={[50, 100, 250, 500]}
                      contentSize={contentSize}
                    />
                  )}
                </>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};
